import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  FilePenLine,
  FolderSync,
  SearchCheck,
  ShieldCheck,
  FileStack,
  Link2,
  Scale,
  Sparkles,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LaborStandaloneLayout from '../../components/labor/LaborStandaloneLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { getLaborIntakeDraft } from '../../features/labor-intake/laborIntakeService';
import { getLaborPortalCopy } from '../../features/labor-intake/laborPortalCopy';

const formatPortalDate = (value, isArabic) =>
  new Intl.DateTimeFormat(isArabic ? 'ar-SA' : 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));

const LaborIntakeEntryPage = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';
  const copy = getLaborPortalCopy(isArabic).entry;
  const draft = getLaborIntakeDraft();

  const actions = [
    {
      title: copy.actions.new.title,
      description: copy.actions.new.description,
      detail: copy.actions.new.detail,
      icon: FilePenLine,
      buttonLabel: copy.actions.new.button,
      onClick: () => navigate('/labor-intake/new'),
      topBarColor: 'bg-[#A27849]',
      iconShell: 'bg-[#f7f1e7] text-[#A27849]',
    },
    {
      title: copy.actions.track.title,
      description: copy.actions.track.description,
      detail: copy.actions.track.detail,
      icon: SearchCheck,
      buttonLabel: copy.actions.track.button,
      onClick: () => navigate('/labor-intake/track'),
      topBarColor: 'bg-[#1F3B64]',
      iconShell: 'bg-[#eef3f9] text-[#1F3B64]',
    },
    {
      title: copy.actions.draft.title,
      description: copy.actions.draft.description,
      detail: draft?._savedAt
        ? `${copy.actions.draft.savedAt}: ${formatPortalDate(draft._savedAt, isArabic)}`
        : copy.actions.draft.detail,
      icon: FolderSync,
      buttonLabel: draft ? copy.actions.draft.resumeButton : copy.actions.draft.button,
      onClick: () => navigate('/labor-intake/draft'),
      topBarColor: 'bg-[#A27849]',
      iconShell: 'bg-[#f7f1e7] text-[#A27849]',
    },
  ];

  return (
    <LaborStandaloneLayout
      badgeText={copy.badgeText}
      footerNote={copy.footerNote}
    >
      <main className="px-4 pb-20 pt-10">
        <section className="mx-auto max-w-7xl">
          <div className="relative overflow-hidden rounded-[34px] border border-[#C8A870]/18 bg-gradient-to-l from-[#142b49] via-[#1F3B64] to-[#2a4d78] px-6 py-9 text-white shadow-[0_26px_80px_rgba(31,59,100,0.18)] sm:px-8 sm:py-10 lg:px-10 lg:py-12">
            <div className="pointer-events-none absolute inset-0">
              <div className="absolute inset-y-0 left-0 w-56 bg-gradient-to-r from-white/10 to-transparent blur-3xl" />
              <div className="absolute -left-10 top-8 h-40 w-40 rounded-full bg-[#C8A870]/10 blur-3xl" />
              <div className="absolute -bottom-16 right-12 h-52 w-52 rounded-full bg-white/8 blur-3xl" />
              <img
                src="/images/wazin-logo.png"
                alt=""
                aria-hidden="true"
                className="absolute left-8 top-1/2 hidden w-[240px] -translate-y-1/2 opacity-[0.045] grayscale md:block lg:w-[300px]"
              />
            </div>

            <div className="relative flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-3xl">
                <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-[#F0DDBE] backdrop-blur-sm">
                  <ShieldCheck className="h-4 w-4" />
                  {copy.pill}
                </span>

                <h1 className="mt-5 text-3xl font-black leading-tight text-white sm:mt-6 sm:text-4xl lg:text-[2.65rem]">
                  {copy.title}
                </h1>

                <p className="mt-5 max-w-2xl text-sm leading-8 text-white sm:mt-6 sm:text-base">
                  {copy.description}
                </p>
              </div>

              <div className="rounded-[26px] border border-white/15 bg-white/10 px-5 py-5 backdrop-blur-md lg:w-[320px]">
                <p className="text-sm font-bold text-[#F0DDBE]">{copy.afterSubmission}</p>
                <div className="mt-4 grid gap-3 text-sm text-white">
                  <div className="flex items-center gap-2.5">
                    <FileStack className="h-4 w-4 text-[#F0DDBE]" />
                    {copy.refBenefit}
                  </div>
                  <div className="flex items-center gap-2.5">
                    <Link2 className="h-4 w-4 text-[#F0DDBE]" />
                    {copy.trackBenefit}
                  </div>
                  <div className="flex items-center gap-2.5">
                    <Scale className="h-4 w-4 text-[#F0DDBE]" />
                    {copy.reviewBenefit}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto mt-9 max-w-7xl">
          <div className="grid gap-6 lg:grid-cols-3">
            {actions.map((action) => (
              <EntryCard key={action.title} {...action} />
            ))}
          </div>
        </section>

        <section className="mx-auto mt-7 max-w-5xl">
          <div className="flex flex-wrap items-center justify-center gap-3 rounded-[24px] border border-[#C8A870]/16 bg-white/80 px-5 py-4 text-center text-sm text-slate-600 shadow-[0_10px_35px_rgba(15,23,42,0.04)] backdrop-blur-sm">
            <span className="inline-flex items-center gap-2 font-semibold text-[#1F3B64]">
              <Sparkles className="h-4 w-4 text-[#A27849]" />
              {copy.trust1}
            </span>
            <span className="hidden h-1 w-1 rounded-full bg-[#C8A870] sm:inline-block" />
            <span>{copy.trust2}</span>
            <span className="hidden h-1 w-1 rounded-full bg-[#C8A870] sm:inline-block" />
            <span>{copy.trust3}</span>
          </div>
        </section>
      </main>
    </LaborStandaloneLayout>
  );
};

const EntryCard = ({
  title,
  description,
  detail,
  icon: Icon,
  buttonLabel,
  onClick,
  topBarColor,
  iconShell,
}) => (
  <Card className="group relative overflow-hidden rounded-[32px] border border-[#dcc8a8] bg-white shadow-[0_18px_45px_rgba(15,23,42,0.08)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_56px_rgba(15,23,42,0.12)]">
    <div className={`absolute inset-x-0 top-0 h-[6px] ${topBarColor}`} />

    <div className="flex min-h-[430px] flex-col">
      <div className="flex min-h-[170px] items-center justify-center px-8 py-6">
        <div className={`flex h-20 w-20 items-center justify-center rounded-full ${iconShell} shadow-sm`}>
          <Icon className="h-9 w-9" />
        </div>
      </div>

      <div className="border-t border-[#e7d8be]" />

      <CardContent className="flex flex-1 flex-col items-center px-7 py-6 text-center">
        <h3 className="min-h-[54px] text-[1.7rem] font-black leading-tight text-[#1F3B64]">
          {title}
        </h3>

        <p className="mt-4 min-h-[54px] text-base font-bold leading-8 text-[#A27849]">
          {description}
        </p>

        <p className="mt-3 min-h-[64px] text-sm leading-8 text-slate-600">
          {detail}
        </p>

        <Button
          type="button"
          onClick={onClick}
          className="mt-auto h-11 min-w-[160px] rounded-xl bg-[#1F3B64] px-5 text-sm font-bold text-white hover:bg-[#183553]"
        >
          {buttonLabel}
          <ChevronLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-0.5" />
        </Button>
      </CardContent>
    </div>
  </Card>
);

export default LaborIntakeEntryPage;
